// JSON to and from /api. GET answers are kept in Cache Storage, so a screen seen once still opens with no signal.
const CACHE = 'api-v1';

export class ApiError extends Error {
  constructor(status, message, details = {}, offline = false) {
    super(message);
    this.status = status;
    this.details = details;
    this.offline = offline;
  }
}

const hasCaches = () => typeof caches !== 'undefined';

async function fromCache(url) {
  if (!hasCaches()) return null;
  try {
    const hit = await (await caches.open(CACHE)).match(url);
    return hit ? await hit.json() : null;
  } catch {
    return null;
  }
}

// Tells the offline bar whether the last answer a screen got came from the offline copy, and of when it is.
const answered = (data) => {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('api-answer', { detail: { offline: !!data.offline, asOf: data.asOf } }));
  return data;
};

async function toCache(url, data) {
  if (!hasCaches()) return;
  try {
    const res = new Response(JSON.stringify(data), { headers: { 'content-type': 'application/json' } });
    await (await caches.open(CACHE)).put(url, res);
  } catch {
    // a full or missing cache only costs the offline copy
  }
}

const GET_TIMEOUT = 8000; // a GET with no answer by then counts as no signal (a weak signal can hang for a minute)
const NO_SESSION_OK = ['/login', '/setup', '/password']; // their 401 is a message for the form, not a lost session

export async function api(method, path, body) {
  const url = new URL('/api' + path, location.origin).href;
  const abort = method === 'GET' ? new AbortController() : null;
  const timer = abort && setTimeout(() => abort.abort(), GET_TIMEOUT);
  let res;
  try {
    res = await fetch(url, {
      method,
      headers: body ? { 'Content-Type': 'application/json' } : {},
      body: body && JSON.stringify(body),
      credentials: 'same-origin',
      signal: abort?.signal,
    });
  } catch (e) {
    if (!(e instanceof TypeError) && e?.name !== 'AbortError') throw e;
    if (method !== 'GET') throw new ApiError(0, 'Nema signala. Pokušaj kad budeš imao signal.', {}, true);
    const cached = await fromCache(url);
    if (cached) return answered({ ...cached, offline: true });
    throw new ApiError(0, 'Nema signala, a ovaj ekran još nije preuzet.', {}, true);
  } finally {
    clearTimeout(timer);
  }
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && !NO_SESSION_OK.includes(path) && typeof window !== 'undefined') {
    window.dispatchEvent(new Event('api-unauthorized')); // the app goes back to sign-in
  }
  if (!res.ok) throw new ApiError(res.status, data.error || 'Greška na serveru. Pokušaj ponovo.', data);
  if (method !== 'GET') return data;
  await toCache(url, data);
  return answered(data);
}

export const get = (path) => api('GET', path);
export const post = (path, body = {}) => api('POST', path, body);
export const put = (path, body = {}) => api('PUT', path, body);
export const del = (path) => api('DELETE', path);

export const clearOfflineCopy = async () => {
  if (hasCaches()) await caches.delete(CACHE);
};
